import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { first, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { Availability } from '../../../../../models/availability';
import { CustomerProductPrice } from '../../../../../models/price';
import { OrderStatistic } from '../../../../../models/order-statistics';
import { Product } from '../../../../../models/product';
import { AuthService } from '../../../../../services/auth/auth.service';
import { AvailabilityService } from '../../../../../services/availability/availability.service';
import { OrderStatisticsService } from '../../../../../services/order-statistics/order-statistics.service';
import { PriceService } from '../../../../../services/price/price.service';
import { ProductListV2ConfigService } from '../../../../../services/product-list-v2-config/product-list-v2-config.service';
import { ProductService } from '../../../../../services/products/product.service';
import { GridViewProductCardV2 } from '../../../../../components/product-grid-view-v2/product-grid-view-v2.component';
import { DynamicPageDcBaseComponent } from '../dynamic-page-dc-base/dynamic-page-dc-base.component';

interface CarouselItem {
  id: string;
  component: any;
  data: GridViewProductCardV2;
  qty: number;
  value: number;
}

type RankBy = 'qty' | 'value';

interface RankedProduct {
  productId: string;
  qty: number;
  value: number;
}

/**
 * The logged-in/selected customer's own best-selling products - ranked by quantity sold or by
 * revenue, switchable at render time - built from json/order-statistics-nostream, the same
 * customer-scoped source TopProductsListConfigService/TopSoldArticlesComponent already expose to
 * any authenticated customer at /top-products (not an admin/dashboard endpoint, so it is safe to
 * call for "my own" data here). Grouping by productModelId and resolving via
 * ProductService.getFullProductsByIds mirrors TopProductsSummaryComponent's own convention for this
 * exact statistics source.
 *
 * <p>Card rendering (price, stock and the buy button) is the carousel block's own established
 * pattern (dynamic-page-dc-product-list-carousel): ProductListV2ConfigService resolves each
 * customer's card component, fed a GridViewProductCardV2 built from price + availability, inside an
 * ngx-owl-carousel-o slider.
 */
@Component({
  selector: 'lib-dynamic-page-dc-best-sellers',
  standalone: false,
  templateUrl: './dynamic-page-dc-best-sellers.component.html',
  styleUrl: './dynamic-page-dc-best-sellers.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class DynamicPageDcBestSellersComponent extends DynamicPageDcBaseComponent implements OnInit {
  private orderStatisticsService = inject(OrderStatisticsService);
  private productService = inject(ProductService);
  private productListV2ConfigService = inject(ProductListV2ConfigService);
  private priceService = inject(PriceService);
  private availabilityService = inject(AvailabilityService);
  private authService = inject(AuthService);
  // gungFlowService is already provided (protected) by DynamicPageDcBaseComponent.

  elementId = 'best-sellers';
  fields = {
    title: 'Bästsäljare',
    limit: 10,
    sinceDays: 365,
    defaultRankBy: 'qty' as RankBy,
    toggleActiveBackground: '#000000',
    toggleActiveColor: '#ffffff',
    toggleInactiveBackground: 'transparent',
    toggleInactiveColor: '#222222',
    toggleBorderRadius: 999,
    // Appended after the sold-value badge ("12 450 kr", "12 450 USD", ...) - left blank, sold
    // quantity is shown without a unit, since this block has no reliable single currency to assume.
    currencyLabel: '',
    itemsMobile: 1,
    itemsTablet: 3,
    itemsDesktop: 4,
    autoplay: false,
    autoplaySpeed: 3,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 0,
    paddingLeft: 0
  };

  rankBy: RankBy = 'qty';
  loading = true;
  carouselAlive = true;
  carouselItems: CarouselItem[] = [];

  private byQty: RankedProduct[] = [];
  private byValue: RankedProduct[] = [];
  private metricsByProductId = new Map<string, RankedProduct>();
  private itemsByProductId = new Map<string, CarouselItem | null>();
  private isSales = false;
  private isAnonymous = true;
  private includeAvailability = false;

  customOptions: OwlOptions = {
    lazyLoad: true,
    loop: false,
    mouseDrag: false,
    touchDrag: false,
    pullDrag: false,
    dots: false,
    navSpeed: 700,
    navText: ['<i class="fas fa-arrow-left"></i>', '<i class="fas fa-arrow-right"></i>'],
    margin: 6,
    responsive: {},
    nav: true
  };

  override ngOnInit(): void {
    super.ngOnInit();
    this.rankBy = (this.element.fields.defaultRankBy as RankBy) || 'qty';
    this.updateCarouselOptions();

    if (this.mode !== 'renderer') {
      return;
    }

    this.authService
      .getRoles()
      .pipe(first())
      .subscribe(roles => {
        this.isSales = roles.some(role => role.toUpperCase() === 'ADMIN' || role.toUpperCase() === 'SALES');
        this.isAnonymous = roles.some(role => role.toUpperCase() === 'ANONYMOUS');
      });

    this.gungFlowService
      .getSelectedFlow()
      .pipe(first())
      .subscribe(flow => (this.includeAvailability = flow.useAvailabilities || flow.requireAvailability));

    this.loadStatistics();
  }

  setRankBy(rankBy: RankBy): void {
    if (this.rankBy === rankBy) {
      return;
    }
    this.rankBy = rankBy;
    this.applyRanking();
  }

  private loadStatistics(): void {
    const sinceDays = Number(this.element.fields.sinceDays) || this.fields.sinceDays;
    const fromDate = this.formatDate(new Date(Date.now() - sinceDays * 24 * 60 * 60 * 1000));

    this.orderStatisticsService
      .getOrderStatistics(fromDate)
      .pipe(first())
      .subscribe({
        next: (statistics: OrderStatistic[]) => {
          this.rankStatistics(statistics || []);
          this.applyRanking();
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  private rankStatistics(statistics: OrderStatistic[]): void {
    const grouped = new Map<string, RankedProduct>();
    for (const row of statistics) {
      const groupId = row.productModelId && row.productModelId !== '-' ? row.productModelId : row.productId;
      if (!groupId) {
        continue;
      }
      const entry = grouped.get(groupId) || { productId: groupId, qty: 0, value: 0 };
      entry.qty += row.orderRowTotalQuantity || 0;
      entry.value += row.orderRowTotalValue || 0;
      grouped.set(groupId, entry);
    }
    const limit = Number(this.element.fields.limit) || this.fields.limit;
    const all = Array.from(grouped.values());
    this.metricsByProductId = grouped;
    this.byQty = [...all].sort((a, b) => b.qty - a.qty).slice(0, limit);
    this.byValue = [...all].sort((a, b) => b.value - a.value).slice(0, limit);
  }

  private applyRanking(): void {
    const ranked = this.rankBy === 'qty' ? this.byQty : this.byValue;
    const ids = ranked.map(r => r.productId);
    if (ids.length === 0) {
      this.carouselItems = [];
      this.loading = false;
      return;
    }
    this.loading = true;
    const missingIds = ids.filter(id => !this.itemsByProductId.has(id));
    const missing$ = missingIds.length > 0 ? this.loadCarouselData(missingIds) : of([]);
    missing$.subscribe(items => {
      const loadedById = new Map(items.map(item => [item.id, item]));
      missingIds.forEach(id => this.itemsByProductId.set(id, loadedById.get(id) ?? null));
      this.carouselItems = ids.map(id => this.itemsByProductId.get(id)).filter((item): item is CarouselItem => !!item);
      this.loading = false;
      this.updateCarouselOptions();
    });
  }

  private loadCarouselData(productIds: string[]): Observable<CarouselItem[]> {
    // getFullProductsByIds, not getProductsByIds: productIds here are productModelIds from order
    // statistics (one row per model, not per sellable SKU), the same resolution
    // TopProductsSummaryComponent uses for this exact data source.
    return this.productService.getFullProductsByIds(productIds).pipe(
      first(),
      switchMap((products: Product[]) =>
        forkJoin({
          products: of(products),
          prices: this.priceService.getCurrentCustomerPrices(productIds).pipe(first()),
          availabilities: this.includeAvailability
            ? this.availabilityService.getAvailabilities(productIds).pipe(first())
            : of([])
        })
      ),
      map(({ products, prices, availabilities }) => {
        const productById = new Map(products.map(product => [product.id, product]));
        return productIds
          .map(id => {
            const product = productById.get(id);
            if (!product) {
              return null;
            }
            const price = (prices as CustomerProductPrice[]).find(p => p && p.productId === id);
            const availability = (availabilities as Availability[]).find(a => a && a.productId === id);
            const itemData: GridViewProductCardV2 = {
              product,
              price,
              availability,
              isSales: this.isSales,
              isAnonymous: this.isAnonymous,
              includeAvailability: this.includeAvailability
            };
            const metrics = this.metricsByProductId.get(id);
            return {
              id,
              component: this.productListV2ConfigService.getProductGridCardComponent(itemData),
              data: itemData,
              qty: metrics?.qty || 0,
              value: metrics?.value || 0
            };
          })
          .filter((item): item is CarouselItem => !!item);
      })
    );
  }

  private updateCarouselOptions(): void {
    this.carouselAlive = false;
    const autoplay = !!this.element.fields.autoplay;
    this.customOptions = {
      ...this.customOptions,
      loop: autoplay,
      autoplay,
      autoplayTimeout: (Number(this.element.fields.autoplaySpeed) || 3) * 1000,
      autoplayHoverPause: true,
      responsive: {
        0: { items: Number(this.element.fields.itemsMobile) || 1 },
        740: { items: Number(this.element.fields.itemsTablet) || 3 },
        940: { items: Number(this.element.fields.itemsDesktop) || 4 }
      }
    };
    setTimeout(() => (this.carouselAlive = true), 100);
  }

  private formatDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }
}
